import { getCurrentContentWindow } from "../../compatManager";
import {
    LegacyDocTypeIdentity,
    LEGACY_DOC_ROW_DOC_EXISTS_FLAG_ATTRS,
    LEGACY_DOC_ROW_DOC_ID_ATTRS,
    LEGACY_DOC_ROW_DOC_NAME_ATTRS,
} from "./types";
export type CompatRetryOptions = {
    tries?: number;
    intervalMs?: number;
};

type CompatWindowGetter = () => any;

/** Shared context for compat automation: retry options + which window to use. */
export type CompatContextOpts = {
    options?: CompatRetryOptions;
    getWindow?: CompatWindowGetter;
};

export function runInCompatWhenReady<T>(
    getValue: () => T | null,
    fn: (value: T) => void,
    tries = 60,
    intervalMs = 250,
) {
    let remaining = tries;
    const tick = () => {
        const v = getValue();
        if (v) return fn(v);
        if (remaining-- <= 0) return;
        window.setTimeout(tick, intervalMs);
    };
    tick();
}

/** Base options for legacy doc automation: context (retry + window) plus doc type identity. */
export type LegacyDocAutomationBaseOpts = CompatContextOpts & LegacyDocTypeIdentity;

function getCompatWindow(): any {
    return getCurrentContentWindow();
}

function isInTPAppFile(w: any, expectedAppId?: string): boolean {
    const root = w?.livedataRoot;
    if (!root || String(root.nodeName) !== "TPAppXML") return false;
    if (expectedAppId) {
        const appId = String(root.getAttribute?.("AppID") ?? "").trim();
        if (appId !== String(expectedAppId).trim()) return false;
    }
    return true;
}

export function openLegacyDocumentsTab(opts?: CompatContextOpts) {
    const tries = opts?.options?.tries ?? 60;
    const intervalMs = opts?.options?.intervalMs ?? 250;
    const getW = opts?.getWindow ?? getCompatWindow;

    runInCompatWhenReady(
        () => {
            const w = getW();
            return (w?.document?.getElementById?.("headingTabDoc") ??
                null) as HTMLElement | null;
        },
        (tab) => tab.click(),
        tries,
        intervalMs,
    );
}

export function clickLegacyElementById(id: string, opts?: CompatContextOpts) {
    const tries = opts?.options?.tries ?? 60;
    const intervalMs = opts?.options?.intervalMs ?? 250;
    const getW = opts?.getWindow ?? getCompatWindow;

    runInCompatWhenReady(
        () => {
            const w = getW();
            return (w?.document?.getElementById?.(id) ?? null) as HTMLElement | null;
        },
        (el) => el.click(),
        tries,
        intervalMs,
    );
}

function hasExistingGeneratedDocFromRow(row: HTMLElement | null): boolean {
    if (!row) return false;
    const node = (row as any).xmlLoopNode ?? (row as any).xmlbaseNode;
    if (!node || typeof node.getAttribute !== "function") return false;

    const read = (attr: string) => String(node.getAttribute(attr) ?? "").trim();
    const readNum = (attr: string) => {
        const v = read(attr);
        const n = Number(v);
        return Number.isFinite(n) ? n : NaN;
    };

    for (const a of LEGACY_DOC_ROW_DOC_ID_ATTRS) {
        const n = readNum(a);
        if (Number.isFinite(n) && n > 0) return true;
    }

    for (const a of LEGACY_DOC_ROW_DOC_NAME_ATTRS) {
        const v = read(a);
        if (v && v !== "0" && v.toLowerCase() !== "null") return true;
    }

    for (const a of LEGACY_DOC_ROW_DOC_EXISTS_FLAG_ATTRS) {
        const v = read(a).toLowerCase();
        if (v === "1" || v === "true" || v === "yes") return true;
    }

    return false;
}

/**
 * Reliable row finder:
 * - does NOT rely on HTML attributes like doctypeid (not present in "All" view)
 * - reads the liveform-bound node (xmlLoopNode/xmlbaseNode) and compares @doctype_id
 */
export function findDocRowByDoctypeId(
    w: any,
    doctypeId: string,
): HTMLElement | null {
    const doc = w?.document;
    if (!doc) return null;

    const container =
        (doc.getElementById?.("documentTypeListByName") as HTMLElement | null) ??
        (doc.getElementById?.("documentListIsland") as HTMLElement | null);

    if (!container) return null;

    for (const el of Array.from(container.querySelectorAll("div"))) {
        const node = (el as any).xmlLoopNode ?? (el as any).xmlbaseNode;
        const id = String(node?.getAttribute?.("doctype_id") ?? "").trim();
        if (id === String(doctypeId).trim()) return el as HTMLElement;
    }
    return null;
}

/**
 * Generates (regenerates) the doctype using the legacy flow:
 * - opens Documents tab
 * - optionally clicks grouping (eg "documentListGroupingMethod2")
 * - finds the row by doctype_id
 * - calls doc_lst_MouseDblClickItem(row, "merge", "1")
 */
// export function openAndGenerateLegacyDocType(opts: LegacyDocAutomationBaseOpts & {
//     onTriggered?: () => void;
// }) {
//     const tries = opts.options?.tries ?? 60;
//     const intervalMs = opts.options?.intervalMs ?? 250;
//     const getW = opts.getWindow ?? getCompatWindow;

//     // Guard: ensure we are actually inside the target file before doing anything.
//     runInCompatWhenReady(
//         () => {
//             const w = getW();
//             return isInTPAppFile(w, opts.expectedAppId) ? w : null;
//         },
//         () => {
//             // 1) open Documents tab
//             runInCompatWhenReady(
//                 () => {
//                     const w = getW();
//                     return (w?.document?.getElementById?.("headingTabDoc") ??
//                         null) as HTMLElement | null;
//                 },
//                 (tab) => tab.click(),
//                 tries,
//                 intervalMs,
//             );

//             // 2) optional grouping click, then wait for list container
//             if (opts.groupingId) {
//                 runInCompatWhenReady(
//                     () => {
//                         const w = getW();
//                         return (w?.document?.getElementById?.(opts.groupingId) ??
//                             null) as HTMLElement | null;
//                     },
//                     (el) => el.click(),
//                     tries,
//                     intervalMs,
//                 );

//                 runInCompatWhenReady(
//                     () => {
//                         const w = getW();
//                         return (w?.document?.getElementById?.("documentTypeListByName") ??
//                             null) as HTMLElement | null;
//                     },
//                     () => {
//                         /* list ready */
//                     },
//                     tries,
//                     intervalMs,
//                 );
//             }

//             // 3) find row + trigger generate
//             runInCompatWhenReady(
//                 () => {
//                     const w = getW();
//                     if (!isInTPAppFile(w, opts.expectedAppId)) return null;
//                     return findDocRowByDoctypeId(w, opts.doctypeId);
//                 },
//                 (row) => {
//                     const w = getW();
//                     if (!isInTPAppFile(w, opts.expectedAppId)) return;

//                     const fn = (w as any).doc_lst_MouseDblClickItem;
//                     if (typeof fn === "function") {
//                         fn(row, "merge", "1"); // regenerate
//                     } else {
//                         row.click(); // fallback: onclick="doc_lst_MouseDblClickItem(this)"
//                     }
//                     try {
//                         opts.onTriggered?.();
//                     } catch {
//                         // ignore
//                     }
//                 },
//                 tries,
//                 intervalMs,
//             );
//         },
//         tries,
//         intervalMs,
//     );
// }

// export function checkLegacyGeneratedDocExists(opts: LegacyDocAutomationBaseOpts & {
//     onResult: (exists: boolean) => void;
// }) {
//     const tries = opts.options?.tries ?? 40;
//     const intervalMs = opts.options?.intervalMs ?? 200;
//     const getW = opts.getWindow ?? getCompatWindow;

//     runInCompatWhenReady(
//         () => {
//             const w = getW();
//             return isInTPAppFile(w, opts.expectedAppId) ? w : null;
//         },
//         () => {
//             // Ensure Documents tab and list are ready (same setup as Generate, but no clicks on row).
//             openLegacyDocumentsTab(opts);
//             if (opts.groupingId) {
//                 clickLegacyElementById(opts.groupingId, opts);
//             }

//             runInCompatWhenReady(
//                 () => {
//                     const w = getW();
//                     if (!isInTPAppFile(w, opts.expectedAppId)) return null;
//                     const row = findDocRowByDoctypeId(w, opts.doctypeId);
//                     if (!row) return null;
//                     return { row, exists: hasExistingGeneratedDocFromRow(row) };
//                 },
//                 (res) => {
//                     try {
//                         opts.onResult(Boolean((res as any).exists));
//                     } catch {
//                         // ignore
//                     }
//                 },
//                 tries,
//                 intervalMs,
//             );
//         },
//         tries,
//         intervalMs,
//     );
// }

// export function openLegacyGeneratedDocByDoctypeId(opts: LegacyDocAutomationBaseOpts & {
//     onOpened?: () => void;
//     onNotGenerated?: () => void;
// }) {
//     const tries = opts.options?.tries ?? 60;
//     const intervalMs = opts.options?.intervalMs ?? 250;
//     const getW = opts.getWindow ?? getCompatWindow;

//     runInCompatWhenReady(
//         () => {
//             const w = getW();
//             return isInTPAppFile(w, opts.expectedAppId) ? w : null;
//         },
//         () => {
//             openLegacyDocumentsTab(opts);
//             if (opts.groupingId) {
//                 clickLegacyElementById(opts.groupingId, opts);
//             }

//             runInCompatWhenReady(
//                 () => {
//                     const w = getW();
//                     if (!isInTPAppFile(w, opts.expectedAppId)) return null;
//                     const row = findDocRowByDoctypeId(w, opts.doctypeId);
//                     if (!row) return null;
//                     return { row, exists: hasExistingGeneratedDocFromRow(row) };
//                 },
//                 (res) => {
//                     const row = (res as any).row as HTMLElement;
//                     const exists = Boolean((res as any).exists);
//                     if (!exists) {
//                         try {
//                             opts.onNotGenerated?.();
//                         } catch {
//                             // ignore
//                         }
//                         return;
//                     }
//                     // Legacy default click path opens the existing doc (or triggers legacy notice).
//                     row.click();
//                     try {
//                         opts.onOpened?.();
//                     } catch {
//                         // ignore
//                     }
//                 },
//                 tries,
//                 intervalMs,
//             );
//         },
//         tries,
//         intervalMs,
//     );
// }

export function emailLegacyGeneratedDocByDoctypeId(opts: LegacyDocAutomationBaseOpts & {
    onEmailed?: () => void;
    onNotGenerated?: () => void;
    onNotSupported?: () => void;
}) {
    const tries = opts.options?.tries ?? 60;
    const intervalMs = opts.options?.intervalMs ?? 250;
    const getW = opts.getWindow ?? getCompatWindow;

    runInCompatWhenReady(
        () => {
            const w = getW();
            return isInTPAppFile(w, opts.expectedAppId) ? w : null;
        },
        () => {
            openLegacyDocumentsTab(opts);
            if (opts.groupingId) {
                clickLegacyElementById(opts.groupingId, opts);
            }

            runInCompatWhenReady(
                () => {
                    const w = getW();
                    if (!isInTPAppFile(w, opts.expectedAppId)) return null;
                    const row = findDocRowByDoctypeId(w, opts.doctypeId);
                    if (!row) return null;
                    return { row, exists: hasExistingGeneratedDocFromRow(row) };
                },
                (res) => {
                    const w = getW();
                    const row = (res as any).row as HTMLElement;
                    const exists = Boolean((res as any).exists);
                    if (!exists) {
                        try {
                            opts.onNotGenerated?.();
                        } catch {
                            // ignore
                        }
                        return;
                    }

                    // Try clicking an explicit Email control inside the row first.
                    const emailButton =
                        (row.querySelector?.('[title*="Email" i]') as HTMLElement | null) ??
                        (row.querySelector?.('[aria-label*="Email" i]') as HTMLElement | null) ??
                        (row.querySelector?.('.fa-paper-plane') as HTMLElement | null) ??
                        null;

                    if (emailButton) {
                        emailButton.click();
                        try {
                            opts.onEmailed?.();
                        } catch {
                            // ignore
                        }
                        return;
                    }

                    // Fallback: attempt to call the legacy handler in "email" mode (if supported).
                    const fn = (w as any).doc_lst_MouseDblClickItem;
                    if (typeof fn === "function") {
                        try {
                            fn(row, "email", "0");
                            opts.onEmailed?.();
                            return;
                        } catch {
                            // ignore and fall through
                        }
                    }

                    try {
                        opts.onNotSupported?.();
                    } catch {
                        // ignore
                    }
                },
                tries,
                intervalMs,
            );
        },
        tries,
        intervalMs,
    );
}

